import * as React from "react";
import dayjs from "dayjs";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Stack,
    Tabs,
    Tab,
    Box,
    Alert,
    Checkbox,
    FormControlLabel,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Typography,
    Divider,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    TableContainer,
    Paper,

    ToggleButton,
    ToggleButtonGroup,
    Chip,
    Grid,
    CircularProgress,
    IconButton,
} from "@mui/material";
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import Papa from "papaparse";
import { alpha } from '@mui/material/styles';
import type { TransactionRule } from './TransactionRulesDialog';

export interface AddTransactionData {
    date: string;
    description: string;
    category: string;
    account: string;
    amount: number;
    currency: string;
}

interface CsvMapping {
    date?: string;
    description?: string;
    amount?: string;
    debit?: string;
    credit?: string;
}

interface AddTransactionDialogProps {
    open: boolean;
    onClose: () => void;
    onAdd: (transaction: AddTransactionData) => void | Promise<void>;
    onBulkAdd?: (transactions: AddTransactionData[]) => Promise<void>;
    availableAccounts?: string[];
    /** Full account metadata used to filter which accounts appear by default */
    accountObjects?: Array<{ name: string; behavior: string }>;
    availableCategories?: string[];
    defaultCurrency?: string;
    existingTransactions?: Array<{
        date: string;
        description: string;
        amount: number;
    }>;
    transactionRules?: TransactionRule[];
}

// Helpers for CSV parsing & heuristics using PapaParse
function detectDelimiter(sample: string) {
    const candidates = [",", ";", "\t", "|"];
    let best = candidates[0];
    let bestCount = 0;
    const firstLine = sample.split(/\r?\n/)[0] ?? sample;
    for (const d of candidates) {
        const cols = firstLine.split(d).length;
        if (cols > bestCount) {
            bestCount = cols;
            best = d;
        }
    }
    return best;
}

function normalizeNumber(s: string) {
    if (s == null) return NaN;
    let t = String(s).trim();
    if (t === "") return NaN;
    // parentheses -> negative
    const paren = /^\((.*)\)$/.exec(t);
    if (paren) t = `-${paren[1]}`;
    // trailing + or - (e.g., 100+ or 100-)
    const trailing = /([0-9.,]+)\s*([+-])$/.exec(t);
    if (trailing) t = `${trailing[2] === '-' ? '-' : ''}${trailing[1]}`;
    // remove currency symbols and spaces
    t = t.replace(/[^0-9.\-]/g, "");
    const n = parseFloat(t);
    return isNaN(n) ? NaN : n;
}

/** Normalize a date string from common CSV formats to YYYY-MM-DD for HTML date inputs. */
function normalizeDate(raw: string): string {
    if (!raw || !raw.trim()) return "";
    const s = raw.trim();

    // Already in YYYY-MM-DD
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;

    // MM/DD/YYYY or M/D/YYYY
    let m = /^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/.exec(s);
    if (m) {
        const parsed = dayjs(`${m[3]}-${m[1].padStart(2, '0')}-${m[2].padStart(2, '0')}`);
        return parsed.isValid() ? parsed.format('YYYY-MM-DD') : s;
    }

    // MM/DD/YY or M/D/YY (2-digit year)
    m = /^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{2})$/.exec(s);
    if (m) {
        // 2-digit year: 00-49 -> 2000s, 50-99 -> 1900s
        const yr = parseInt(m[3], 10);
        const fullYear = yr < 50 ? 2000 + yr : 1900 + yr;
        const parsed = dayjs(`${fullYear}-${m[1].padStart(2, '0')}-${m[2].padStart(2, '0')}`);
        return parsed.isValid() ? parsed.format('YYYY-MM-DD') : s;
    }

    // YYYY/MM/DD
    m = /^(\d{4})[\/](\d{1,2})[\/](\d{1,2})$/.exec(s);
    if (m) {
        const parsed = dayjs(`${m[1]}-${m[2].padStart(2, '0')}-${m[3].padStart(2, '0')}`);
        return parsed.isValid() ? parsed.format('YYYY-MM-DD') : s;
    }

    // DD-Mon-YYYY or DD/Mon/YYYY (e.g. 14-Jan-2026)
    m = /^(\d{1,2})[\/\-]([A-Za-z]{3,})[\/\-](\d{2,4})$/.exec(s);
    if (m) {
        const parsed = dayjs(s);
        return parsed.isValid() ? parsed.format('YYYY-MM-DD') : s;
    }

    // Fallback: let dayjs try to parse it
    const fallback = dayjs(s);
    return fallback.isValid() ? fallback.format('YYYY-MM-DD') : s;
}

export default function AddTransactionDialog({
    open,
    onClose,
    onAdd,
    onBulkAdd,
    availableAccounts = [],
    accountObjects = [] as Array<{ name: string; behavior: string }>,
    availableCategories = [],
    defaultCurrency = "USD",
    existingTransactions = [],
    transactionRules = [],
}: AddTransactionDialogProps) {
    const [tab, setTab] = React.useState(0);
    const [showAllAccounts, setShowAllAccounts] = React.useState(false);

    // Accounts with 'cash', 'credit', or 'liability' behavior are shown by default.
    // Assets (real estate, vehicles) and investments are hidden unless the user expands.
    const COMMON_BEHAVIORS = new Set(['cash', 'credit', 'liability']);
    const commonAccountNames = React.useMemo(() => {
        if (!accountObjects.length) return availableAccounts;
        return accountObjects.filter(a => COMMON_BEHAVIORS.has(a.behavior)).map(a => a.name);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [accountObjects, availableAccounts]);
    const hasHiddenAccounts = React.useMemo(() =>
        accountObjects.some(a => !COMMON_BEHAVIORS.has(a.behavior)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [accountObjects]);
    // Returns the list of account names to render in a Select, always including currentValue.
    const visibleAccountList = (currentValue: string) => {
        const base = showAllAccounts || !hasHiddenAccounts ? availableAccounts : commonAccountNames;
        if (currentValue && !base.includes(currentValue)) return [...base, currentValue];
        return base;
    };

    const [newTx, setNewTx] = React.useState<AddTransactionData>({
        date: dayjs().format("YYYY-MM-DD"),
        description: "",
        category: "",
        account: commonAccountNames[0] ?? availableAccounts[0] ?? "",
        amount: 0,
        currency: defaultCurrency,
    });
    // manual amount as string so user can clear leading zeroes
    const [manualAmount, setManualAmount] = React.useState<string>("");
    const [txType, setTxType] = React.useState<'expense' | 'income'>('expense');

    React.useEffect(() => {
        setNewTx((p) => ({
            ...p,
            account: p.account || commonAccountNames[0] || availableAccounts[0] || "",
            currency: p.currency || defaultCurrency,
        }));
    }, [availableAccounts, availableCategories, defaultCurrency, commonAccountNames]);

    // CSV state
    const [csvText, setCsvText] = React.useState<string | null>(null);
    const [csvHeaders, setCsvHeaders] = React.useState<string[]>([]);
    const [csvRows, setCsvRows] = React.useState<string[][]>([]);
    const [csvMapping, setCsvMapping] = React.useState<CsvMapping>({});
    const [csvStatus, setCsvStatus] = React.useState<string | null>(null);
    const [csvFileName, setCsvFileName] = React.useState<string | null>(null);
    const [amountMode, setAmountMode] = React.useState<'amount' | 'debitcredit'>('amount');

    const headerNumberHints = React.useMemo(() => {
        return csvHeaders.map((h, idx) => {
            const samples: number[] = [];
            for (let r = 0; r < Math.min(20, csvRows.length); r++) {
                const v = csvRows[r][idx];
                const n = normalizeNumber(v ?? '');
                if (!isNaN(n)) samples.push(n);
            }
            return { header: h, parsedCount: samples.length, sample: samples[0] };
        });
    }, [csvHeaders, csvRows]);
    const [delimiter, setDelimiter] = React.useState<string>("auto");
    const [hasHeader, setHasHeader] = React.useState<boolean>(true);
    const [parsedPreview, setParsedPreview] = React.useState<
        Array<{ date: string; description: string; amount: number; raw: string[]; error?: string }>
    >([]);
    const [reviewMode, setReviewMode] = React.useState<boolean>(false);
    const [reviewLoading, setReviewLoading] = React.useState<boolean>(false);
    const [csvAccount, setCsvAccount] = React.useState<string>(availableAccounts[0] ?? "");
    const [csvDefaultCategory, setCsvDefaultCategory] = React.useState<string>("");
    type ReviewRow = {
        id: number;
        selected: boolean;
        date: string;
        description: string;
        amount: number;
        category: string;
        categorySource: 'rule' | 'default' | 'manual' | 'none';
        raw: string[];
        error?: string;
        duplicate?: boolean;
    };
    const [reviewRows, setReviewRows] = React.useState<ReviewRow[]>([]);
    const [filterMode, setFilterMode] = React.useState<'all' | 'duplicates' | 'errors' | 'unflagged'>('all');
    const [reviewPage, setReviewPage] = React.useState(0);
    const REVIEW_PAGE_SIZE = 50;

    // Memoize filtered rows, pagination, and summary stats to avoid recomputing in render
    const filteredReviewRows = React.useMemo(() => {
        return reviewRows.filter(r => {
            if (filterMode === 'all') return true;
            if (filterMode === 'duplicates') return !!r.duplicate;
            if (filterMode === 'errors') return !!r.error;
            if (filterMode === 'unflagged') return !r.duplicate && !r.error;
            return true;
        });
    }, [reviewRows, filterMode]);

    const reviewPageCount = Math.max(1, Math.ceil(filteredReviewRows.length / REVIEW_PAGE_SIZE));
    const clampedPage = Math.min(reviewPage, reviewPageCount - 1);
    const pagedReviewRows = React.useMemo(() => {
        const start = clampedPage * REVIEW_PAGE_SIZE;
        return filteredReviewRows.slice(start, start + REVIEW_PAGE_SIZE);
    }, [filteredReviewRows, clampedPage]);

    const reviewSummary = React.useMemo(() => ({
        total: reviewRows.length,
        duplicates: reviewRows.filter(r => r.duplicate).length,
        errors: reviewRows.filter(r => r.error).length,
        categorized: reviewRows.filter(r => r.category).length,
        selected: reviewRows.filter(r => r.selected).length,
        ruleMatched: reviewRows.filter(r => r.categorySource === 'rule').length,
    }), [reviewRows]);

    // Reset page when filter changes
    React.useEffect(() => { setReviewPage(0); }, [filterMode]);

    // Keep csvAccount in sync when availableAccounts changes
    React.useEffect(() => {
        if (!csvAccount) {
            const def = commonAccountNames[0] ?? availableAccounts[0];
            if (def) setCsvAccount(def);
        }
    }, [availableAccounts, commonAccountNames]);

    /** Match a transaction description (and optionally account) against saved rules. */
    const matchRule = React.useCallback(
        (description: string, account: string): { categoryName: string; matched: boolean } => {
            for (const rule of transactionRules) {
                const fieldValue =
                    rule.field === 'description' ? description :
                    rule.field === 'account' ? account :
                    '';
                const fv = rule.caseSensitive ? fieldValue : fieldValue.toLowerCase();
                const rv = rule.caseSensitive ? rule.value : rule.value.toLowerCase();
                const hit = rule.operator === 'contains' ? fv.includes(rv) : fv === rv;
                if (hit) return { categoryName: rule.categoryName, matched: true };
            }
            return { categoryName: '', matched: false };
        },
        [transactionRules]
    );
    const headerSamples = React.useMemo(() => csvHeaders.map((h, idx) => csvRows[0]?.[idx] ?? ''), [csvHeaders, csvRows]);

    const reviewTimeoutRef = React.useRef<number | null>(null);
    React.useEffect(() => {
        return () => {
            if (reviewTimeoutRef.current) window.clearTimeout(reviewTimeoutRef.current);
        };
    }, []);

    // drag/drop state and file input ref for CSV region
    const inputFileRef = React.useRef<HTMLInputElement | null>(null);
    const [dragActive, setDragActive] = React.useState(false);
    const onRegionClick = () => inputFileRef.current?.click();
    const onRegionDragOver = (e: React.DragEvent) => { e.preventDefault(); setDragActive(true); };
    const onRegionDragLeave = (e: React.DragEvent) => { e.preventDefault(); setDragActive(false); };
    const onRegionDrop = (e: React.DragEvent) => { e.preventDefault(); setDragActive(false); const f = e.dataTransfer.files?.[0]; if (f) handleCsvFile(f); };

    const handleTabChange = (_e: React.SyntheticEvent, v: number) => setTab(v);

    const handleSubmit = () => {
        if (!newTx.description || !newTx.account) return;
        // parse manualAmount to number if set
        const amt = manualAmount !== '' ? Math.abs(parseFloat(manualAmount) || 0) : Math.abs(newTx.amount || 0);
        const storedAmount = txType === 'expense' ? -Math.abs(amt) : Math.abs(amt);
        const txToAdd = { ...newTx, amount: storedAmount };
        onAdd(txToAdd);
        setNewTx({
            date: dayjs().format("YYYY-MM-DD"),
            description: "",
            category: "",
            account: commonAccountNames[0] ?? availableAccounts[0] ?? "",
            amount: 0,
            currency: defaultCurrency,
        });
        setManualAmount("");
        setTxType('expense');
        onClose();
    };

    const handleCsvFile = (file?: File | null) => {
        if (!file) {
            setCsvText(null);
            setCsvHeaders([]);
            setCsvRows([]);
            setCsvMapping({});
            setCsvStatus(null);
            setCsvFileName(null);
            return;
        }
        const reader = new FileReader();
        reader.onload = () => {
            const text = String(reader.result ?? "");
            setCsvText(text);
            setCsvStatus("Loaded file — detecting format...");
            setCsvFileName(file.name);
        };
        reader.readAsText(file);
    };

    // Suggest mapping from headers
    function suggestMapping(headers: string[]): CsvMapping {
        const h = headers.map((x) => x.toLowerCase());
        const find = (cands: string[]) => {
            for (const c of cands) {
                const idx = h.findIndex((hh) => hh.includes(c));
                if (idx >= 0) return headers[idx];
            }
            return undefined;
        };
        const date = find(["date", "posted", "transaction date", "txn date"]);
        const description = find(["description", "desc", "memo", "details", "payee"]);
        const amount = find(["amount", "amt", "value", "transaction amount", "amounts"]);
        const debit = find(["debit", "dr"]);
        const credit = find(["credit", "cr"]);
        return { date, description, amount, debit, credit };
    }

    function parseRow(row: string[], headers: string[], mapping: CsvMapping) {
        const get = (col?: string) => {
            if (!col) return undefined;
            const idx = headers.indexOf(col);
            return idx >= 0 ? row[idx] : undefined;
        };
        let amount = NaN;
        if (mapping.amount) {
            amount = normalizeNumber(get(mapping.amount) ?? "");
        } else if (mapping.debit || mapping.credit) {
            const d = normalizeNumber(get(mapping.debit) ?? "0") || 0;
            const c = normalizeNumber(get(mapping.credit) ?? "0") || 0;
            amount = c - d;
        } else {
            // try to auto-detect amount column (first column that parses to a number)
            for (let i = 0; i < row.length; i++) {
                const n = normalizeNumber(row[i]);
                if (!isNaN(n)) {
                    amount = n;
                    break;
                }
            }
        }

        const rawDate = get(mapping.date) ?? row[0] ?? "";
        const date = normalizeDate(rawDate);
        const description = get(mapping.description) ?? row[1] ?? row.slice(2).join(" ") ?? "";
        const error = isNaN(amount) ? "Could not parse amount" : undefined;
        return { date, description, amount: isNaN(amount) ? 0 : amount, raw: row, error };
    }

    // Re-parse whenever CSV text, delimiter, or header mode changes
    React.useEffect(() => {
        if (!csvText) return;
        const det = delimiter === "auto" ? detectDelimiter(csvText) : delimiter;
        try {
            const res = Papa.parse(csvText, { delimiter: det, skipEmptyLines: true, header: hasHeader });
            let headers: string[] = [];
            let rows: string[][] = [];
            if (hasHeader) {
                headers = (res.meta && (res.meta as any).fields) || [];
                rows = (res.data as any[]).map((r: any) => headers.map((h) => String(r[h] ?? "")));
            } else {
                rows = (res.data as any[]).map((r: any[]) => r.map((c) => String(c ?? "")));
                if (rows.length > 0) {
                    headers = rows[0].map((_c, i) => `Column ${i + 1}`);
                }
            }
            // ensure headers length matches row width
            if (rows.length > 0 && headers.length < rows[0].length) {
                headers = rows[0].map((_c, i) => headers[i] ?? `Column ${i + 1}`);
            }

            setCsvHeaders(headers);
            setCsvRows(rows);
            const suggested = suggestMapping(headers);
            // Only set suggested mapping if the user hasn't already chosen mappings.
            setCsvMapping((prev) => {
                const hasAny = !!(prev && (prev.date || prev.description || prev.amount || prev.debit || prev.credit));
                return hasAny ? prev : suggested;
            });
            setCsvStatus(`Parsed ${rows.length} rows using delimiter '${det === "\t" ? "tab" : det}'`);
        } catch (err: any) {
            setCsvStatus(`Parse error: ${String(err?.message ?? err)}`);
            setCsvHeaders([]);
            setCsvRows([]);
            setParsedPreview([]);
        }
    }, [csvText, delimiter, hasHeader]);

    // Rebuild preview when rows or mapping change (separate effect to avoid update loops)
    React.useEffect(() => {
        if (!csvRows || csvRows.length === 0) {
            setParsedPreview([]);
            return;
        }
        const mappingToUse = csvMapping && Object.keys(csvMapping).some((k) => !!(csvMapping as any)[k]) ? csvMapping : suggestMapping(csvHeaders);
        const preview = csvRows.slice(0, 50).map((r) => parseRow(r, csvHeaders, mappingToUse));
        setParsedPreview(preview);
    }, [csvRows, csvMapping, csvHeaders]);

    // Note: CSV import now happens via the review UI (`handleImportSelected`).

    const handleReviewCsv = () => {
        if (csvRows.length === 0) return;
        // Defer heavy parsing briefly so the spinner can render, improving perceived performance
        setReviewLoading(true);
        setReviewMode(true);
        setCsvStatus(`Preparing to parse ${csvRows.length} rows...`);

        reviewTimeoutRef.current = window.setTimeout(() => {
            // Build full parsed list (not just preview) and detect duplicates/errors
            const headers = csvHeaders;
            const mappingToUse = csvMapping && Object.keys(csvMapping).some((k) => !!(csvMapping as any)[k]) ? csvMapping : suggestMapping(csvHeaders);
            const full = csvRows.map((r) => parseRow(r, headers, mappingToUse));

            // Duplicate detection by key: date|amount|normalized description
            const keyCounts: Record<string, number> = {};
            const normalizeDesc = (s: string) => (String(s || '')).toLowerCase().replace(/\s+/g, ' ').trim();
            
            // First, add all existing transactions to keyCounts
            existingTransactions.forEach((tx) => {
                const key = `${tx.date || ''}|${Number(tx.amount).toFixed(2)}|${normalizeDesc(tx.description)}`;
                keyCounts[key] = (keyCounts[key] || 0) + 1;
            });
            
            // Then check CSV rows against existing + within CSV
            full.forEach((p) => {
                const key = `${p.date || ''}|${Number(p.amount).toFixed(2)}|${normalizeDesc(p.description)}`;
                keyCounts[key] = (keyCounts[key] || 0) + 1;
            });

            const rows: ReviewRow[] = full.map((p, i) => {
                const key = `${p.date || ''}|${Number(p.amount).toFixed(2)}|${normalizeDesc(p.description)}`;
                const ruleMatch = matchRule(p.description || '', csvAccount);
                let category = '';
                let categorySource: ReviewRow['categorySource'] = 'none';
                if (ruleMatch.matched) {
                    category = ruleMatch.categoryName;
                    categorySource = 'rule';
                } else if (csvDefaultCategory) {
                    category = csvDefaultCategory;
                    categorySource = 'default';
                }
                return {
                    id: i,
                    selected: true,
                    date: p.date || dayjs().format('YYYY-MM-DD'),
                    description: p.description || '',
                    amount: p.amount || 0,
                    category,
                    categorySource,
                    raw: p.raw,
                    error: p.error,
                    duplicate: (keyCounts[key] || 0) > 1,
                };
            });

            setReviewRows(rows);
            setCsvStatus(`Ready to review ${rows.length} transactions`);
            setReviewLoading(false);
            reviewTimeoutRef.current = null;
        }, 20);
    };

    const handleToggleSelectAll = (checked: boolean) => {
        const filteredIds = new Set(filteredReviewRows.map(r => r.id));
        setReviewRows((prev) => prev.map((r) =>
            filteredIds.has(r.id) ? { ...r, selected: checked } : r
        ));
    };

    const handleToggleFilterCategory = (mode: 'duplicates' | 'errors' | 'unflagged', selected: boolean) => {
        setReviewRows((prev) => prev.map((r) => {
            const matches =
                mode === 'duplicates' ? !!r.duplicate :
                mode === 'errors' ? !!r.error :
                !r.duplicate && !r.error;
            return matches ? { ...r, selected } : r;
        }));
    };

    const handleToggleRow = (id: number) => {
        setReviewRows((prev) => prev.map((r) => (r.id === id ? { ...r, selected: !r.selected } : r)));
    };

    const handleUpdateRow = (id: number, fields: Partial<{ date: string; description: string; amount: number; category: string }>) => {
        setReviewRows((prev) => prev.map((r) => {
            if (r.id !== id) return r;
            const updated = { ...r, ...fields };
            // If category was explicitly changed, mark as manual
            if ('category' in fields) {
                updated.categorySource = 'manual';
            }
            // If description changed, re-evaluate rule match (unless category was manually set)
            if ('description' in fields && r.categorySource !== 'manual') {
                const ruleMatch = matchRule(fields.description || r.description, csvAccount);
                if (ruleMatch.matched) {
                    updated.category = ruleMatch.categoryName;
                    updated.categorySource = 'rule';
                } else if (csvDefaultCategory) {
                    updated.category = csvDefaultCategory;
                    updated.categorySource = 'default';
                } else {
                    updated.category = '';
                    updated.categorySource = 'none';
                }
            }
            return updated;
        }));
    };

    const handleImportSelected = async () => {
        const toImport = reviewRows.filter((r) => r.selected).map((r) => ({ ...r }));
        if (toImport.length === 0) {
            setCsvStatus('No rows selected for import');
            return;
        }

        // Validate that we have an account selected
        if (!csvAccount) {
            setCsvStatus('Error: No account selected. Please select an account.');
            return;
        }

        const toTransactions: AddTransactionData[] = toImport.map((row) => ({
            date: row.date || dayjs().format('YYYY-MM-DD'),
            description: row.description || 'Imported',
            category: row.category || '',
            account: csvAccount,
            amount: row.amount || 0,
            currency: defaultCurrency,
        }));

        try {
            // Use bulk import if available and more than 1 transaction, otherwise sequential
            if (onBulkAdd && toTransactions.length > 1) {
                await onBulkAdd(toTransactions);
            } else {
                // Import transactions sequentially to handle async properly
                for (const t of toTransactions) {
                    await onAdd(t);
                }
            }
            setCsvStatus(`Imported ${toTransactions.length} transactions`);
            setReviewMode(false);
            // Close the dialog after successful import (cleans up CSV state via handleClose)
            handleClose();
        } catch (error) {
            console.error('Error importing transactions:', error);
            setCsvStatus(`Error importing transactions: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
    };

    // Reset CSV/import state when the dialog is closed or import completes
    const resetCsvState = () => {
        setCsvText(null);
        setCsvHeaders([]);
        setCsvRows([]);
        setCsvMapping({});
        setCsvStatus(null);
        setCsvFileName(null);
        setParsedPreview([]);
        setReviewMode(false);
        setReviewRows([]);
        setReviewPage(0);
        setReviewLoading(false);
        setFilterMode('all');
        setDelimiter('auto');
        setHasHeader(true);
        setAmountMode('amount');
        setCsvAccount(availableAccounts[0] ?? '');
        setCsvDefaultCategory('');
        // clear file input if present
        try {
            if (inputFileRef.current) inputFileRef.current.value = '';
        } catch (e) {
            // ignore
        }
        if (reviewTimeoutRef.current) {
            window.clearTimeout(reviewTimeoutRef.current);
            reviewTimeoutRef.current = null;
        }
    };

    const handleClose = () => {
        resetCsvState();
        setTab(0);
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            fullWidth
            maxWidth="md"
            sx={{
                    '& .MuiDialog-container': { alignItems: 'flex-start' },
                    // Limit the top margin to the dialog paper only so inner Paper components (summary cards, tables) are not pushed down
                    '& .MuiDialog-paper': { mt: 6 },
                }}
        >
            <DialogTitle>Add Transaction</DialogTitle>

            <Tabs value={tab} onChange={handleTabChange} variant="fullWidth">
                <Tab label="Manual" />
                <Tab label="CSV Upload" />
                <Tab label="Link Account" />
            </Tabs>

            <DialogContent dividers sx={{ minHeight: 480 }}>
                {tab === 0 && (
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <TextField
                            label="Date"
                            type="date"
                            value={newTx.date}
                            onChange={(e) => setNewTx((p) => ({ ...p, date: e.target.value }))}
                            InputLabelProps={{ shrink: true }}
                            fullWidth
                        />

                        <TextField
                            label="Description"
                            value={newTx.description}
                            onChange={(e) => setNewTx((p) => ({ ...p, description: e.target.value }))}
                            fullWidth
                        />

                        <FormControl fullWidth>
                            <InputLabel>Category</InputLabel>
                            <Select
                                value={newTx.category}
                                label="Category"
                                onChange={(e) => setNewTx((p) => ({ ...p, category: String(e.target.value) }))}
                                size="small"
                                displayEmpty
                                renderValue={(selected) => {
                                    const v = String(selected ?? '');
                                    return v === '' ? 'Unselected' : v;
                                }}
                            >
                                <MenuItem value="">Unselected</MenuItem>
                                {availableCategories.map((c) => (
                                    <MenuItem key={c} value={c}>
                                        {c}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl fullWidth>
                            <InputLabel>Account</InputLabel>
                            <Select
                                value={newTx.account}
                                label="Account"
                                onChange={(e) => {
                                    if (String(e.target.value) === '__toggle_accounts__') {
                                        setShowAllAccounts(v => !v);
                                        return;
                                    }
                                    setNewTx((p) => ({ ...p, account: String(e.target.value) }));
                                }}
                                size="small"
                            >
                                {visibleAccountList(newTx.account).map((a) => (
                                    <MenuItem key={a} value={a}>{a}</MenuItem>
                                ))}
                                {hasHiddenAccounts && (
                                    <MenuItem value="__toggle_accounts__" sx={{ color: 'primary.main', fontSize: '0.8rem', fontStyle: 'italic' }}>
                                        {showAllAccounts ? '▲ Show fewer accounts' : '▼ Show all accounts'}
                                    </MenuItem>
                                )}
                            </Select>
                        </FormControl>

                        <FormControl fullWidth>
                            <InputLabel>Type</InputLabel>
                            <Select
                                value={txType}
                                label="Type"
                                onChange={(e) => setTxType(e.target.value as 'expense' | 'income')}
                                size="small"
                            >
                                <MenuItem value="expense">Expense</MenuItem>
                                <MenuItem value="income">Income</MenuItem>
                            </Select>
                        </FormControl>

                        <Stack direction="row" spacing={2} alignItems="center">
                            <TextField
                                label="Amount"
                                type="number"
                                inputProps={{ min: 0, step: '0.01' }}
                                value={manualAmount}
                                onChange={(e) => setManualAmount(e.target.value)}
                                fullWidth
                                sx={{ input: { color: txType === 'income' ? 'success.main' : 'error.main' } }}
                                placeholder="0.00"
                            />

                            <FormControl sx={{ minWidth: 120 }}>
                                <InputLabel>Currency</InputLabel>
                                <Select
                                    value={newTx.currency}
                                    label="Currency"
                                    onChange={(e) => setNewTx((p) => ({ ...p, currency: String(e.target.value) }))}
                                    size="small"
                                >
                                    <MenuItem value="USD">USD</MenuItem>
                                    <MenuItem value="EUR">EUR</MenuItem>
                                    <MenuItem value="GBP">GBP</MenuItem>
                                </Select>
                            </FormControl>
                        </Stack>
                    </Stack>
                )}

                {/* ======== */}
                {/* Tab 1 - Importing transactions via CSV */}
                {/* ======== */}
                {tab === 1 && (
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        {/* If in mapping mode (not review mode) */}
                        {!reviewMode && (
                            <Box sx={{ position: 'relative' }}>
                                {/* If there is a CSV file uploaded show format controls and mapping */}
                                <Typography variant="h6">Upload a CSV file.</Typography>
                                <Box sx={{ mt: 1 }}>
                                    {/* See if a CSV file has been selected */}
                                    {!csvText ? (
                                        <Box
                                            onClick={onRegionClick}
                                            onDragOver={onRegionDragOver}
                                            onDragLeave={onRegionDragLeave}
                                            onDrop={onRegionDrop}
                                            sx={{
                                                width: '100%',
                                                border: '2px dashed',
                                                borderColor: dragActive ? 'primary.main' : 'grey.300',
                                                bgcolor: dragActive ? 'action.hover' : 'background.paper',
                                                borderRadius: 2,
                                                p: 4,
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                cursor: 'pointer',
                                                '&:hover': { bgcolor: 'action.hover', borderColor: 'primary.main' }
                                            }}
                                        >
                                            <UploadFileIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
                                            <Typography variant="h6">Click to upload or drag and drop</Typography>
                                            <Typography variant="body2" color="text.secondary">CSV or TXT files only</Typography>
                                            <input ref={inputFileRef} type="file" hidden accept=".csv,text/csv" onChange={(e) => handleCsvFile(e.target.files?.[0] ?? null)} />
                                        </Box>
                                    ) : (
                                        <Box sx={{ border: '2px dashed', borderColor: 'success.main', bgcolor: (theme) => alpha(theme.palette.success.main, 0.1), p: 3, borderRadius: 2 }}>
                                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                                                <CheckCircleOutlineIcon sx={{ color: 'success.main', fontSize: 48 }} />
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <UploadFileIcon color="action" />
                                                    <Typography sx={{ fontWeight: 600 }}>{csvFileName ?? 'CSV file'}</Typography>
                                                </Box>
                                                <Button variant="outlined" color="success" onClick={(e) => { e.preventDefault(); handleCsvFile(null); }}>Choose Different File</Button>
                                            </Box>
                                        </Box>
                                    )}
                                </Box>
                                {csvHeaders.length > 0 && (
                                    <Box>
                                        <Box sx={{ p: 2, mb: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider', bgcolor: (theme) => alpha(theme.palette.divider, 0.03) }}>
                                            <Typography variant="h6" sx={{ mb: 1 }}>Delimiter</Typography>

                                            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
                                                <FormControl sx={{ minWidth: 160 }}>
                                                    <Select value={delimiter} size="small" onChange={(e) => setDelimiter(String(e.target.value))}>
                                                        <MenuItem value="auto">Auto</MenuItem>
                                                        <MenuItem value=",">Comma (,)</MenuItem>
                                                        <MenuItem value=";">Semicolon (;)</MenuItem>
                                                        <MenuItem value="\t">Tab</MenuItem>
                                                        <MenuItem value="|">Pipe (|)</MenuItem>
                                                    </Select>
                                                </FormControl>

                                                <FormControlLabel control={<Checkbox checked={hasHeader} onChange={(e) => setHasHeader(e.target.checked)} />} label="Has header row" />
                                            </Stack>
                                        </Box>

                                        <Typography variant="h6">Map Columns</Typography>
                                        <Typography variant="caption" sx={{ display: 'block', mb: 1 }}>
                                            Select which CSV columns map to which fields.
                                        </Typography>

                                        <Stack direction="column" spacing={2} sx={{ mb: 1 }}>
                                            <FormControl sx={{ m: 1, minWidth: 120 }}>
                                                <InputLabel id="label-map-date">Date Column</InputLabel>
                                                <Select
                                                    labelId="label-map-date"
                                                    value={csvMapping.date ?? ""}
                                                    label="Date Column *"
                                                    onChange={(e) => setCsvMapping((p) => ({ ...p, date: String(e.target.value) }))}
                                                >
                                                    {csvHeaders.map((h, idx) => (
                                                        <MenuItem key={h} value={h}>
                                                            <Stack direction="row" spacing={1} alignItems="center">
                                                                <span>{h}</span>
                                                                <Chip size="small" label={headerSamples[idx] ? `Eg: ${headerSamples[idx]}` : ''} sx={{ ml: 1 }} />
                                                            </Stack>
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>

                                            <FormControl sx={{ m: 1, minWidth: 120 }}>
                                                <InputLabel id="label-map-desc">Description Column</InputLabel>
                                                <Select
                                                    labelId="label-map-desc"
                                                    value={csvMapping.description ?? ""}
                                                    label="Description Column *"
                                                    onChange={(e) => setCsvMapping((p) => ({ ...p, description: String(e.target.value) }))}
                                                >
                                                    {csvHeaders.map((h, idx) => (
                                                        <MenuItem key={h} value={h}>
                                                            <Stack direction="row" spacing={1} alignItems="center">
                                                                <span>{h}</span>
                                                                <Chip size="small" label={headerSamples[idx] ? `Eg: ${headerSamples[idx]}` : ''} sx={{ ml: 1 }} />
                                                            </Stack>
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Stack>

                                        <Divider sx={{ mb: 1 }} />

                                        <Stack direction="column" spacing={1} sx={{ mb: 1 }}>
                                            <Stack direction="row" alignItems="center" spacing={2}>
                                                <Typography variant="h6" sx={{ minWidth: 160, mb: 0 }}>Amount mapping mode</Typography>
                                                <ToggleButtonGroup
                                                    value={amountMode}
                                                    exclusive
                                                    onChange={(_e, v) => {
                                                        if (!v) return;
                                                        const vv = v as 'amount' | 'debitcredit';
                                                        setAmountMode(vv);
                                                        if (vv === 'amount') setCsvMapping((p) => ({ ...p, debit: undefined, credit: undefined }));
                                                        else setCsvMapping((p) => ({ ...p, amount: undefined }));
                                                    }}
                                                    size="small"
                                                >
                                                    <ToggleButton value="amount">Unified</ToggleButton>
                                                    <ToggleButton value="debitcredit">Debit / Credit</ToggleButton>
                                                </ToggleButtonGroup>
                                            </Stack>

                                            <Typography variant="caption" color="text.secondary">
                                                <strong>Unified:</strong> single numeric column with +/- or parentheses to indicate negative values.{' '}
                                                <strong>Debit/Credit:</strong> separate columns for credits and debits.
                                            </Typography>
                                        </Stack>

                                        {amountMode === 'amount' ? (
                                            <Stack direction="row" spacing={2} sx={{ mb: 1 }}>
                                                <FormControl fullWidth>
                                                    <InputLabel id="label-map-amount">Amount Column</InputLabel>
                                                    <Select
                                                        labelId="label-map-amount"
                                                        value={csvMapping.amount ?? ""}
                                                        label="Amount Column"
                                                        // size="small"
                                                        onChange={(e) => setCsvMapping((p) => ({ ...p, amount: String(e.target.value), debit: undefined, credit: undefined }))}
                                                    >
                                                        {csvHeaders.map((h) => (
                                                            <MenuItem key={h} value={h}>
                                                                <Stack direction="row" spacing={1} alignItems="center">
                                                                    <span>{h}</span>
                                                                    <Chip size="small" label={
                                                                        (() => {
                                                                            const hint = headerNumberHints.find((x) => x.header === h);
                                                                            if (!hint) return '';
                                                                            return hint.parsedCount > 0 ? `${hint.parsedCount}/${Math.min(20, csvRows.length)} numbers` : 'no numbers';
                                                                        })()
                                                                    } sx={{ ml: 1 }} />
                                                                </Stack>
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            </Stack>
                                        ) : (
                                            <Stack direction="row" spacing={2} sx={{ mb: 1 }}>
                                                <FormControl fullWidth>
                                                    <InputLabel id="label-map-debit">Debit Column</InputLabel>
                                                    <Select
                                                        labelId="label-map-debit"
                                                        value={csvMapping.debit ?? ""}
                                                        label="Debit Column"
                                                        // displayEmpty
                                                        onChange={(e) => setCsvMapping((p) => ({ ...p, debit: String(e.target.value), amount: undefined }))}
                                                    // size="small"
                                                    >
                                                        {csvHeaders.map((h) => (
                                                            <MenuItem key={h} value={h}>
                                                                <Stack direction="row" spacing={1} alignItems="center">
                                                                    <span>{h}</span>
                                                                    <Chip size="small" label={(() => {
                                                                        const hint = headerNumberHints.find((x) => x.header === h);
                                                                        // if a credit column is already selected, show combined count for this candidate debit + selected credit
                                                                        if (csvMapping.credit) {
                                                                            const debitIdx = csvHeaders.indexOf(h);
                                                                            const creditIdx = csvHeaders.indexOf(csvMapping.credit);
                                                                            if (debitIdx >= 0 && creditIdx >= 0) {
                                                                                let cnt = 0;
                                                                                for (let r = 0; r < Math.min(20, csvRows.length); r++) {
                                                                                    const row = csvRows[r] ?? [];
                                                                                    const d = normalizeNumber(row[debitIdx] ?? "");
                                                                                    const c = normalizeNumber(row[creditIdx] ?? "");
                                                                                    if (!isNaN(d) || !isNaN(c)) cnt++;
                                                                                }
                                                                                return cnt > 0 ? `${cnt}/${Math.min(20, csvRows.length)} numbers` : 'no numbers';
                                                                            }
                                                                        }
                                                                        if (!hint) return '';
                                                                        return hint.parsedCount > 0 ? `${hint.parsedCount}/${Math.min(20, csvRows.length)} numbers` : 'no numbers';
                                                                    })()} sx={{ ml: 1 }} />
                                                                </Stack>
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>

                                                <FormControl fullWidth>
                                                    <InputLabel id="label-map-credit">Credit Column</InputLabel>
                                                    <Select
                                                        labelId="label-map-credit"
                                                        value={csvMapping.credit ?? ""}
                                                        label="Credit Column"
                                                        // displayEmpty
                                                        onChange={(e) => setCsvMapping((p) => ({ ...p, credit: String(e.target.value), amount: undefined }))}
                                                    // size="small"
                                                    >
                                                        {csvHeaders.map((h) => (
                                                            <MenuItem key={h} value={h}>
                                                                <Stack direction="row" spacing={1} alignItems="center">
                                                                    <span>{h}</span>
                                                                    <Chip size="small" label={(() => {
                                                                        const hint = headerNumberHints.find((x) => x.header === h);
                                                                        // if a debit column is already selected, show combined count for selected debit + this candidate credit
                                                                        if (csvMapping.debit) {
                                                                            const creditIdx = csvHeaders.indexOf(h);
                                                                            const debitIdx = csvHeaders.indexOf(csvMapping.debit);
                                                                            if (debitIdx >= 0 && creditIdx >= 0) {
                                                                                let cnt = 0;
                                                                                for (let r = 0; r < Math.min(20, csvRows.length); r++) {
                                                                                    const row = csvRows[r] ?? [];
                                                                                    const d = normalizeNumber(row[debitIdx] ?? "");
                                                                                    const c = normalizeNumber(row[creditIdx] ?? "");
                                                                                    if (!isNaN(d) || !isNaN(c)) cnt++;
                                                                                }
                                                                                return cnt > 0 ? `${cnt}/${Math.min(20, csvRows.length)} numbers` : 'no numbers';
                                                                            }
                                                                        }
                                                                        if (!hint) return '';
                                                                        return hint.parsedCount > 0 ? `${hint.parsedCount}/${Math.min(20, csvRows.length)} numbers` : 'no numbers';
                                                                    })()} sx={{ ml: 1 }} />
                                                                </Stack>
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            </Stack>
                                        )}

                                        <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
                                            Number parsing notes: we strip currency symbols and thousands separators, accept parentheses for negatives (e.g. (100)), trailing +/- (e.g. 100+ or 100-), and leading minus signs (e.g. -100). If a column shows "no numbers" you can try switching delimiter or picking a different column.
                                        </Typography>

                                        <Typography variant="subtitle2" sx={{ mt: 1 }}>Preview (first 5 rows)</Typography>
                                        <TableContainer component={Paper} sx={{ maxHeight: 220 }}>
                                            <Table size="small">
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell>Date</TableCell>
                                                        <TableCell>Description</TableCell>
                                                        <TableCell>Amount</TableCell>
                                                        <TableCell>Error</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {parsedPreview.slice(0, 5).map((p, i) => (
                                                        <TableRow key={i}>
                                                            <TableCell>{p.date}</TableCell>
                                                            <TableCell sx={{ maxWidth: 300, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.description}</TableCell>
                                                            <TableCell>{p.amount}</TableCell>
                                                            <TableCell>{p.error}</TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>

                                        {csvStatus && <Alert severity="info">{csvStatus}</Alert>}
                                    </Box>
                                )}
                            </Box>
                        )}

                        {/* If instead in review mode */}
                        {reviewMode && (
                            <Box>
                                {/* Account & Category selectors */}
                                <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                                    <Typography variant="subtitle2" sx={{ mb: 1.5 }}>Import Settings</Typography>
                                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                        <FormControl size="small" sx={{ minWidth: 200 }}>
                                            <InputLabel>Account</InputLabel>
                                            <Select
                                                value={csvAccount}
                                                label="Account"
                                                onChange={(e) => {
                                                    const newAcct = String(e.target.value);
                                                    if (newAcct === '__toggle_accounts__') {
                                                        setShowAllAccounts(v => !v);
                                                        return;
                                                    }
                                                    setCsvAccount(newAcct);
                                                    // Re-evaluate rules for account-based matches
                                                    setReviewRows(prev => prev.map(r => {
                                                        if (r.categorySource === 'manual') return r;
                                                        const ruleMatch = matchRule(r.description, newAcct);
                                                        if (ruleMatch.matched) return { ...r, category: ruleMatch.categoryName, categorySource: 'rule' as const };
                                                        if (csvDefaultCategory) return { ...r, category: csvDefaultCategory, categorySource: 'default' as const };
                                                        return { ...r, category: '', categorySource: 'none' as const };
                                                    }));
                                                }}
                                            >
                                                {visibleAccountList(csvAccount).map((a) => (
                                                    <MenuItem key={a} value={a}>{a}</MenuItem>
                                                ))}
                                                {hasHiddenAccounts && (
                                                    <MenuItem value="__toggle_accounts__" sx={{ color: 'primary.main', fontSize: '0.8rem', fontStyle: 'italic' }}>
                                                        {showAllAccounts ? '▲ Show fewer accounts' : '▼ Show all accounts'}
                                                    </MenuItem>
                                                )}
                                            </Select>
                                        </FormControl>
                                        <FormControl size="small" sx={{ minWidth: 200 }}>
                                            <InputLabel>Default Category</InputLabel>
                                            <Select
                                                value={csvDefaultCategory}
                                                label="Default Category"
                                                onChange={(e) => {
                                                    const newCat = String(e.target.value);
                                                    setCsvDefaultCategory(newCat);
                                                    // Update rows that aren't rule-matched or manually set
                                                    setReviewRows(prev => prev.map(r => {
                                                        if (r.categorySource === 'rule' || r.categorySource === 'manual') return r;
                                                        return { ...r, category: newCat, categorySource: newCat ? 'default' as const : 'none' as const };
                                                    }));
                                                }}
                                                displayEmpty
                                                renderValue={(selected) => {
                                                    const v = String(selected ?? '');
                                                    return v === '' ? 'None (use rules only)' : v;
                                                }}
                                            >
                                                <MenuItem value="">None (use rules only)</MenuItem>
                                                {availableCategories.map((c) => (
                                                    <MenuItem key={c} value={c}>{c}</MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Stack>
                                    {transactionRules.length > 0 && (
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                                            {reviewSummary.ruleMatched} of {reviewSummary.total} transactions matched by {transactionRules.length} saved rule{transactionRules.length !== 1 ? 's' : ''}
                                        </Typography>
                                    )}
                                </Paper>

                                {/* Summary cards */}
                                <Grid container columns={12} spacing={2} sx={{ mb: 2 }}>
                                    <Grid size={{ xs: 12, sm: 3 }}>
                                        <Paper sx={{ p: 2, textAlign: 'center' }} variant="outlined">
                                            <Typography variant="caption" color="text.secondary">Total transactions</Typography>
                                            <Typography variant="h6">{reviewSummary.total}</Typography>
                                        </Paper>
                                    </Grid>
                                    <Grid size={{ xs: 12, sm: 3 }}>
                                        <Paper sx={{ p: 2, textAlign: 'center' }} variant="outlined">
                                            <Typography variant="caption" color="text.secondary">Potential duplicates</Typography>
                                            <Typography variant="h6">{reviewSummary.duplicates}</Typography>
                                        </Paper>
                                    </Grid>
                                    <Grid size={{ xs: 12, sm: 3 }}>
                                        <Paper sx={{ p: 2, textAlign: 'center' }} variant="outlined">
                                            <Typography variant="caption" color="text.secondary">Possible errors</Typography>
                                            <Typography variant="h6">{reviewSummary.errors}</Typography>
                                        </Paper>
                                    </Grid>
                                    <Grid size={{ xs: 12, sm: 3 }}>
                                        <Paper sx={{ p: 2, textAlign: 'center' }} variant="outlined">
                                            <Typography variant="caption" color="text.secondary">Categorized</Typography>
                                            <Typography variant="h6">{reviewSummary.categorized}</Typography>
                                        </Paper>
                                    </Grid>
                                </Grid>

                                {/* Filters + actions */}
                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center" sx={{ mb: 1 }}>
                                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                        <Button variant={filterMode === 'all' ? 'contained' : 'outlined'} size="small" onClick={() => setFilterMode('all')}>All</Button>
                                        <Button variant={filterMode === 'duplicates' ? 'contained' : 'outlined'} size="small" onClick={() => setFilterMode('duplicates')}>Duplicates</Button>
                                        {reviewSummary.duplicates > 0 && (
                                            <Stack direction="row" spacing={0.5}>
                                                <Button size="small" sx={{ minWidth: 0, px: 0.5, fontSize: '0.7rem' }} onClick={() => handleToggleFilterCategory('duplicates', true)}>✓ all</Button>
                                                <Button size="small" sx={{ minWidth: 0, px: 0.5, fontSize: '0.7rem' }} onClick={() => handleToggleFilterCategory('duplicates', false)}>✗ all</Button>
                                            </Stack>
                                        )}
                                        <Button variant={filterMode === 'errors' ? 'contained' : 'outlined'} size="small" onClick={() => setFilterMode('errors')}>Errors</Button>
                                        {reviewSummary.errors > 0 && (
                                            <Stack direction="row" spacing={0.5}>
                                                <Button size="small" sx={{ minWidth: 0, px: 0.5, fontSize: '0.7rem' }} onClick={() => handleToggleFilterCategory('errors', true)}>✓ all</Button>
                                                <Button size="small" sx={{ minWidth: 0, px: 0.5, fontSize: '0.7rem' }} onClick={() => handleToggleFilterCategory('errors', false)}>✗ all</Button>
                                            </Stack>
                                        )}
                                        <Button variant={filterMode === 'unflagged' ? 'contained' : 'outlined'} size="small" onClick={() => setFilterMode('unflagged')}>Unflagged</Button>
                                        {reviewSummary.errors === 0 && reviewSummary.duplicates === 0 ? null : (
                                            <Stack direction="row" spacing={0.5}>
                                                <Button size="small" sx={{ minWidth: 0, px: 0.5, fontSize: '0.7rem' }} onClick={() => handleToggleFilterCategory('unflagged', true)}>✓ all</Button>
                                                <Button size="small" sx={{ minWidth: 0, px: 0.5, fontSize: '0.7rem' }} onClick={() => handleToggleFilterCategory('unflagged', false)}>✗ all</Button>
                                            </Stack>
                                        )}
                                    </Stack>
                                    <Box sx={{ flex: 1 }} />
                                </Stack>

                                {/* Transactions table with select-all in header */}
                                <TableContainer component={Paper} sx={{ maxHeight: 420 }}>
                                    <Table size="small" stickyHeader>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell padding="checkbox">
                                                    <Checkbox
                                                        indeterminate={reviewSummary.selected > 0 && reviewSummary.selected < reviewSummary.total}
                                                        checked={reviewSummary.total > 0 && reviewSummary.selected === reviewSummary.total}
                                                        onChange={(e) => handleToggleSelectAll(e.target.checked)}
                                                    />
                                                </TableCell>
                                                <TableCell>Date</TableCell>
                                                <TableCell>Description</TableCell>
                                                <TableCell align="right">Amount</TableCell>
                                                <TableCell>Category</TableCell>
                                                <TableCell>Notes</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {pagedReviewRows.map((r) => (
                                                <TableRow
                                                    key={r.id}
                                                    hover
                                                    sx={{
                                                        ...(r.error ? { backgroundColor: (theme) => alpha(theme.palette.error.main, 0.08) } : {}),
                                                        ...(!r.error && r.duplicate ? { backgroundColor: (theme) => alpha(theme.palette.warning.main, 0.06) } : {}),
                                                    }}
                                                >
                                                    <TableCell padding="checkbox">
                                                        <Checkbox checked={r.selected} onChange={() => handleToggleRow(r.id)} />
                                                    </TableCell>
                                                    <TableCell sx={{ width: 170 }}>
                                                        <TextField type="date" size="small" value={r.date} onChange={(e) => handleUpdateRow(r.id, { date: e.target.value })} />
                                                    </TableCell>
                                                    <TableCell sx={{ maxWidth: 520 }}>
                                                        <TextField fullWidth size="small" value={r.description} onChange={(e) => handleUpdateRow(r.id, { description: e.target.value })} />
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        <TextField type="number" size="small" value={String(r.amount)} onChange={(e) => handleUpdateRow(r.id, { amount: Number(e.target.value) || 0 })} sx={{ input: { textAlign: 'right' } }} />
                                                    </TableCell>
                                                    <TableCell sx={{ minWidth: 160 }}>
                                                        <Stack direction="row" spacing={0.5} alignItems="center">
                                                            <Select
                                                                size="small"
                                                                value={r.category}
                                                                onChange={(e) => handleUpdateRow(r.id, { category: String(e.target.value) })}
                                                                displayEmpty
                                                                renderValue={(v) => {
                                                                    const val = String(v ?? '');
                                                                    return val === '' ? <Typography variant="body2" color="text.secondary">—</Typography> : val;
                                                                }}
                                                                sx={{ minWidth: 120 }}
                                                            >
                                                                <MenuItem value="">—</MenuItem>
                                                                {availableCategories.map((c) => (
                                                                    <MenuItem key={c} value={c}>{c}</MenuItem>
                                                                ))}
                                                            </Select>
                                                            {r.categorySource === 'rule' && <Chip label="rule" size="small" color="info" variant="outlined" />}
                                                        </Stack>
                                                    </TableCell>
                                                    <TableCell>
                                                        {r.duplicate && <Chip label="duplicate" size="small" color="warning" sx={{ mr: 1 }} />}
                                                        {r.error && <Chip label={r.error} size="small" color="error" />}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                                {/* Pagination controls */}
                                {filteredReviewRows.length > REVIEW_PAGE_SIZE && (
                                    <Stack direction="row" spacing={1} alignItems="center" justifyContent="center" sx={{ mt: 1 }}>
                                        <IconButton size="small" disabled={clampedPage === 0} onClick={() => setReviewPage(p => Math.max(0, p - 1))}>
                                            <NavigateBeforeIcon />
                                        </IconButton>
                                        <Typography variant="body2">
                                            {clampedPage * REVIEW_PAGE_SIZE + 1}–{Math.min((clampedPage + 1) * REVIEW_PAGE_SIZE, filteredReviewRows.length)} of {filteredReviewRows.length}
                                        </Typography>
                                        <IconButton size="small" disabled={clampedPage >= reviewPageCount - 1} onClick={() => setReviewPage(p => Math.min(reviewPageCount - 1, p + 1))}>
                                            <NavigateNextIcon />
                                        </IconButton>
                                    </Stack>
                                )}
                                {reviewLoading && (
                                    <Box sx={{ position: 'absolute', inset: 0, zIndex: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: (theme) => alpha(theme.palette.background.paper, 0.6) }}>
                                        <Stack spacing={1} alignItems="center">
                                            <CircularProgress />
                                            <Typography variant="body2">Processing {csvRows.length} rows…</Typography>
                                        </Stack>
                                    </Box>
                                )}
                            </Box>
                        )}
                    </Stack>
                )}

                {/* ======== */}
                {/* Tab 2 - Linking an account */}
                {/* ======== */}
                {tab === 2 && (
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <Alert severity="info">Linking accounts is not implemented yet. This will allow you to import transactions directly from your bank in the future.</Alert>
                        <Typography variant="body2">Placeholder: connect to Plaid/Bank or provide provider-specific instructions here.</Typography>
                    </Stack>
                )}
            </DialogContent>

                <DialogActions>
                <Button onClick={handleClose}>Cancel</Button>
                {tab === 0 ? (
                    <Button variant="contained" onClick={handleSubmit}>
                        Add
                    </Button>
                ) : tab === 1 ? (
                    reviewMode ? (
                        <><Button variant="contained" onClick={() => setReviewMode(false)}>
                            Back
                        </Button><Button variant="contained" color="success" onClick={handleImportSelected} disabled={reviewRows.filter(r => r.selected).length === 0}>
                                Import Selected
                            </Button></>
                    ) : (
                        <Button variant="contained" onClick={handleReviewCsv} disabled={csvRows.length === 0}>
                            Review Transactions
                        </Button>
                    )
                ) : (
                    <Button variant="contained" disabled>
                        Link Account (Coming Soon)
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
}

